import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormPageLayout } from "../../../components/forms/FormPageLayout";
import { FormSection } from "../../../components/forms/FormSection";
import {
  LocationPicker,
  type LocationPickerValue,
} from "../../../components/forms/LocationPicker";
import {
  useCreateEventLocation,
  useUpdateEventLocation,
} from "../../../hooks/useEventLocationMutations";
import { useSnackbar } from "../../../context/SnackbarContext";
import { useUnsavedChangesGuard } from "../../../hooks/useUnsavedChangesGuard";
import {
  locationFormSchema,
  type LocationFormData,
} from "../../../schemas/locationFormSchema";
import type { EventLocation } from "@event-planner/shared";

const DEFAULT_LOCATION_COLOR = "#607D8B";

interface LocationFormProps {
  mode: "create" | "edit";
  initialData?: EventLocation;
}

/**
 * Standalone Location create/edit page. Top-level collection page,
 * follows the schema-driven forms convention (ADR-011): Zod form
 * schema for validation, RHF for state and error display, embeds the
 * shared LocationPicker for the actual editing UX.
 *
 * The picker is the same component used in EventForm / SessionForm /
 * GroupForm / TravelForm — the only difference here is that this page
 * IS the canonical edit surface for a location, so the picker's
 * "Linked — edits apply everywhere" banner is suppressed by passing
 * locationId=null in the picker value (the redundancy isn't useful on
 * the dedicated edit page).
 */
export function LocationForm({ mode, initialData }: LocationFormProps) {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { create, loading: creating } = useCreateEventLocation();
  const { update, loading: updating } = useUpdateEventLocation();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<LocationFormData>({
    resolver: zodResolver(locationFormSchema),
    defaultValues: initialData
      ? {
          label: initialData.label,
          coordinates: initialData.coordinates,
          address: initialData.address,
          city: initialData.city,
          stateOrRegion: initialData.stateOrRegion,
          country: initialData.country,
          color: initialData.color,
          verified: initialData.verified,
        }
      : {
          label: "",
          coordinates: null,
          address: null,
          city: null,
          stateOrRegion: null,
          country: null,
          color: DEFAULT_LOCATION_COLOR,
          verified: false,
        },
  });

  const { dialog: unsavedGuardDialog } = useUnsavedChangesGuard(
    form.formState.isDirty,
    form.formState.isSubmitting || creating || updating,
  );

  // Bridge form state ↔ picker value. The form schema is the canonical
  // shape (validated, typed); the picker emits LocationPickerValue,
  // which we project onto the form fields via setValue. Same pattern
  // EventForm uses for its picker.
  const formData = form.watch();
  const hasContent =
    !!formData.label ||
    !!formData.coordinates ||
    !!formData.city ||
    !!formData.stateOrRegion ||
    !!formData.country ||
    !!formData.address;
  const pickerValue: LocationPickerValue = hasContent
    ? {
        // `locationId` is the id of the record this page IS editing, so
        // the picker can (a) exclude self-references from its "Existing
        // locations" suggestions and (b) auto-fire MapKit autocomplete
        // for unverified label-only rows. `originalLocationId` stays
        // null because LocationForm is the canonical edit surface — we
        // don't want the picker's "Editing/New copy" toggle to show
        // here (it's keyed off originalLocationId via canFork).
        locationId: initialData?.id ?? null,
        originalLocationId: null,
        verified: formData.verified,
        label: formData.label,
        coordinates: formData.coordinates,
        address: formData.address,
        city: formData.city,
        stateOrRegion: formData.stateOrRegion,
        country: formData.country,
        color: formData.color,
      }
    : null;

  function handlePickerChange(next: LocationPickerValue) {
    if (!next) {
      form.reset({
        label: "",
        coordinates: null,
        address: null,
        city: null,
        stateOrRegion: null,
        country: null,
        color: DEFAULT_LOCATION_COLOR,
        verified: false,
      });
      return;
    }
    form.setValue("label", next.label);
    form.setValue("coordinates", next.coordinates);
    form.setValue("address", next.address);
    form.setValue("city", next.city);
    form.setValue("stateOrRegion", next.stateOrRegion);
    form.setValue("country", next.country);
    form.setValue("color", next.color);
    form.setValue("verified", next.verified);
  }

  async function onSubmit(data: LocationFormData) {
    if (!eventId) return;
    setServerError(null);
    try {
      if (mode === "create") {
        await create({ eventId, ...data });
        showSuccess("Location created");
      } else if (initialData) {
        await update(initialData.id, data);
        showSuccess("Location updated");
      }
      navigate(`/events/${eventId}/locations`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      setServerError(msg);
      showError(msg);
    }
  }

  return (
    <>
      {unsavedGuardDialog}
      <FormPageLayout
        title={mode === "create" ? "New Location" : "Edit Location"}
        onSubmit={form.handleSubmit(onSubmit)}
        error={serverError}
        submitting={creating || updating}
        submitLabel={mode === "create" ? "Create Location" : "Save Changes"}
        testIdSlug="location"
      >
        <FormSection title="Location">
          {/* Controller wraps `label` so RHF tracks form-level errors on
              the required-label rule. The actual editing happens through
              the picker, which writes label / coords / address / color /
              verified collectively via handlePickerChange. */}
          <Controller
            name="label"
            control={form.control}
            render={() => (
              <LocationPicker
                eventId={eventId ?? ""}
                value={pickerValue}
                onChange={handlePickerChange}
                defaultColor={DEFAULT_LOCATION_COLOR}
                testIdSuffix="location"
                mapHeight={320}
                layout="split"
              />
            )}
          />
        </FormSection>
      </FormPageLayout>
    </>
  );
}
